import { useEffect, useState } from "react";
import PlatformFooter from "@/components/PlatformFooter";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { http } from "@/lib/api";
import Billing from "@/pages/Billing";
import { usePortal, fmtDate } from "@/components/SubscriptionTab";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, LogOut, ExternalLink, Loader2, UserCircle2, AlertTriangle } from "lucide-react";

export default function TrialExpired({ status }) {
  const { logout, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [sub, setSub] = useState(null);
  const portal = usePortal();
  const s = status?.status;
  const admin = user?.role === "TENANT_ADMIN";
  const title = s === "SUSPENDED" ? t("billing.suspended_title")
    : s === "TRIAL_EXPIRED" ? t("billing.trial_expired_title")
      : s === "PAST_DUE" ? t("billing.past_due_title") : t("billing.expired_title");

  useEffect(() => { http.get("/billing/subscription").then((r) => setSub(r.data)).catch(() => setSub({})); }, []);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 flex flex-col items-center p-4 sm:p-6" data-testid="trial-expired-page">
      <div className="max-w-3xl w-full space-y-6 py-8">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-400/30 flex items-center justify-center mx-auto">
            <Lock className="w-8 h-8 text-amber-300" />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-sky-300">NEXTMIND INFISSI</div>
          <h1 className="font-heading text-2xl font-extrabold" data-testid="paywall-title">{title}</h1>
          <p className="text-slate-400 flex items-center justify-center gap-2 text-sm">
            <ShieldCheck className="w-4 h-4 text-emerald-400" /> {t("billing.data_safe")}
          </p>
        </div>

        {/* Profile + billing management: always reachable when paywalled */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="paywall-profile">
          <div className="flex items-center gap-3 min-w-0">
            <UserCircle2 className="w-9 h-9 text-slate-400 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold truncate">{user?.name || user?.email}</div>
              <div className="text-xs text-slate-400 truncate">{user?.email} · {status?.name}</div>
              {sub?.current_period_end && s !== "TRIAL_EXPIRED" && (
                <div className="text-xs text-slate-400">{t("sub.ends")}: {fmtDate(sub.current_period_end, i18n.language)}</div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {admin && sub?.portal_available && (
              <Button data-testid="paywall-portal-btn" onClick={portal.open} disabled={portal.busy} className="bg-sky-500 hover:bg-sky-400 text-[#0B1120] font-semibold gap-1.5">
                {portal.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />} {t("sub.manage")}
              </Button>
            )}
            <Button data-testid="trial-logout-btn" variant="ghost" className="text-slate-300 hover:bg-white/10" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" /> {t("common.logout")}
            </Button>
          </div>
        </div>

        {s === "PAST_DUE" && (
          <p className="text-sm text-orange-300 flex items-center gap-2" data-testid="paywall-past-due-hint"><AlertTriangle className="w-4 h-4" /> {t("sub.past_due_hint")}</p>
        )}

        {admin ? <Billing dark /> : (
          <p className="text-sm text-slate-400 text-center" data-testid="paywall-operator-hint">{t("sub.operator_hint")}</p>
        )}
      </div>
      <PlatformFooter dark />
    </div>
  );
}
